import { type Profile, profileDefaults, Access } from '../../profile/profile.models'
import { OTPService, type OTPCode } from './otp.service'
import type { HttpAdapter } from '../../core/adapters/axios.adapter'
import type { AnalyticsService } from '../../core/services/analytics.service'

/**
 * Custom error thrown when an email address is not found.
 */

export class AuthService {
  readonly TBWJF = 'tbw-auth-infos'
  private _profile: Profile = profileDefaults
  private analyticsService?: AnalyticsService
  constructor(private _httpAdapter: HttpAdapter, private _otpService: OTPService) { }

  bindAnalytics(analyticsService: AnalyticsService) {
    this.analyticsService = analyticsService
  }

  get isAuthenticated() {
    return this._profile.userAccess !== Access.Visitor
  }

  get accountType() {
    if (!this.profile) { return null }
    return this.profile.userAccess
  }

  get profile(): Profile {
    return this._profile
  }

  get isFreemium(): boolean {
    const freemium = this.profile.userAccess === Access.Free
    return this.isAuthenticated && freemium
  }

  get isPremium(): boolean {
    return this.isAuthenticated && (this.profile.userAccess === Access.Premium || this.profile.userAccess === Access.Founder)
  }

  get isVisitor(): boolean {
    return !this.isAuthenticated
  }

  notify(authenticated: boolean) {
    const customEvent = new CustomEvent('authChange', {
      detail: { authenticated },
      bubbles: true,
      cancelable: true
    });

    document.dispatchEvent(customEvent);
  }

  async sendEmail(email: string) {
    const response = await this._httpAdapter.post('query', { email });
    return response.data
  }

  register(token: string) {
    localStorage.setItem(this.TBWJF, token)
  }

  logout() {
    if (this.isAuthenticated) {
      this.analyticsService?.track('logout')
      this.analyticsService?.clearUserContext()
    }
    this.clear()
  }

  async verifyOTP(email: string, otpCode: OTPCode) {
    const res = await this._otpService.verifyOTP(email, otpCode)
    if (res) {
      this.register(res.token)
      await this.setProfile(res.token)
      this.analyticsService?.setUserContext()
      this.analyticsService?.track('login')
    }
    return res
  }

  async setProfile(token: string) {
    const response = await this._httpAdapter.get<Profile>('', { headers: { Authorization: `Bearer ${token}` } });
    this._profile = response.data as Profile;
  }

  private clear() {
    this._profile = profileDefaults;
    localStorage.removeItem(this.TBWJF);
    this.notify(false);
  }

  private getFromStorage(): string | null {
    return localStorage.getItem(this.TBWJF);
  }

  async checkAuth() {
    const token = this.getFromStorage();

    if (!token) {
      this.notify(false);
      return false
    }

    try {
      this.notify(true);
      await this.setProfile(token)
      this.analyticsService?.setUserContext()
      return true;
    } catch (response) {
      console.error('auth check failed', response)
      this.clear();
      return false
    }

  }
}
